import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SearchEngine from './SearchEngine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

// Sanitize PII (Personally Identifiable Information) from public display
const sanitizePII = (text) => {
  if (!text || typeof text !== 'string') return text;
  let sanitized = text;
  sanitized = sanitized.replace(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g, '[email hidden]');
  sanitized = sanitized.replace(/(\+?\d{1,3}[\s\-]?)?\(?\d{2,5}\)?[\s\-]?\d{3,5}[\s\-]?\d{3,5}/g, (match) => {
    const digits = match.replace(/\D/g, '');
    if (digits.length >= 7 && digits.length <= 15) return '[phone hidden]';
    return match;
  });
  sanitized = sanitized.replace(/whatsapp\s*:?\s*\+?\d[\d\s\-]{7,14}/gi, '[WhatsApp hidden]');
  return sanitized;
};

// ============================================================================
// DAY-OF-WEEK THEME CONFIG
// ============================================================================
const DAY_THEMES = [
  {
    key: 'sunday', className: 'day-theme-sunday',
    graha_en: 'Surya', graha_ta: 'சூரியன்', graha_symbol: '☀️',
    greeting_en: 'Blessed Sunday — Day of Surya (Sun)',
    greeting_ta: 'இனிய ஞாயிற்றுக்கிழமை — சூரிய வாரம்',
    deity_en: 'Lord Surya Narayana', deity_ta: 'சூரிய நாராயணன்',
  },
  {
    key: 'monday', className: 'day-theme-monday',
    graha_en: 'Chandra', graha_ta: 'சந்திரன்', graha_symbol: '🌙',
    greeting_en: 'Blessed Monday — Day of Chandra (Moon)',
    greeting_ta: 'இனிய திங்கட்கிழமை — சந்திர வாரம்',
    deity_en: 'Lord Shiva', deity_ta: 'சிவபெருமான்',
  },
  {
    key: 'tuesday', className: 'day-theme-tuesday',
    graha_en: 'Mangala', graha_ta: 'செவ்வாய்', graha_symbol: '🔴',
    greeting_en: 'Blessed Tuesday — Day of Mangala (Mars)',
    greeting_ta: 'இனிய செவ்வாய்க்கிழமை — செவ்வாய் வாரம்',
    deity_en: 'Lord Subrahmanya', deity_ta: 'சுப்ரமண்யர்',
  },
  {
    key: 'wednesday', className: 'day-theme-wednesday',
    graha_en: 'Budha', graha_ta: 'புதன்', graha_symbol: '🟢',
    greeting_en: 'Blessed Wednesday — Day of Budha (Mercury)',
    greeting_ta: 'இனிய புதன்கிழமை — புதன் வாரம்',
    deity_en: 'Lord Vishnu', deity_ta: 'ஸ்ரீமந் நாராயணன்',
  },
  {
    key: 'thursday', className: 'day-theme-thursday',
    graha_en: 'Guru', graha_ta: 'குரு', graha_symbol: '🟠',
    greeting_en: 'Blessed Thursday — Day of Guru (Jupiter)',
    greeting_ta: 'இனிய வியாழக்கிழமை — குரு வாரம்',
    deity_en: 'Lord Dakshinamoorthy', deity_ta: 'தக்ஷிணாமூர்த்தி',
  },
  {
    key: 'friday', className: 'day-theme-friday',
    graha_en: 'Shukra', graha_ta: 'சுக்கிரன்', graha_symbol: '🌸',
    greeting_en: 'Blessed Friday — Day of Shukra (Venus)',
    greeting_ta: 'இனிய வெள்ளிக்கிழமை — சுக்கிர வாரம்',
    deity_en: 'Goddess Lakshmi', deity_ta: 'ஸ்ரீ மஹாலக்ஷ்மி',
  },
  {
    key: 'saturday', className: 'day-theme-saturday',
    graha_en: 'Shani', graha_ta: 'சனி', graha_symbol: '🪐',
    greeting_en: 'Blessed Saturday — Day of Shani (Saturn)',
    greeting_ta: 'இனிய சனிக்கிழமை — சனி வாரம்',
    deity_en: 'Lord Hanuman', deity_ta: 'ஆஞ்சநேயர்',
  }
];

const getTodayTheme = () => DAY_THEMES[new Date().getDay()];

// ============================================================================
// CATEGORY SECTIONS CONFIG — maps menu headings to content
// ============================================================================
const CATEGORY_SECTIONS = [
  {
    id: 'anmikam',
    icon: '🛕',
    title_en: 'Anmikam — Spiritual Articles',
    title_ta: 'ஆன்மிகம் — ஆன்மீகக் கட்டுரைகள்',
    desc_en: '108 Divyadesams, Sathya Narayana Pooja, and spiritual articles',
    desc_ta: '108 திவ்யதேசங்கள், சத்ய நாராயண பூஜை, ஆன்மீகக் கட்டுரைகள்',
    link: '/divyadesams',
    viewAllLink: '/blog',
    viewAllText_en: 'All Articles',
    viewAllText_ta: 'அனைத்து கட்டுரைகள்',
    images: [
      '/images/rituals/kalyana-uthsavam.jpg',
      '/images/rituals/naandhi.jpg',
      '/images/sathya-narayana.jpg',
    ],
    subLinks: [
      { slug: '/divyadesams', label_en: '108 Divyadesams', label_ta: '108 திவ்யதேசங்கள்', icon: '🏛️' },
      { slug: '/sathya-narayana-pooja', label_en: 'Sathya Narayana Pooja', label_ta: 'ஸத்ய நாராயண பூஜை', icon: '🙏' },
      { slug: '/blog', label_en: 'Anubava Arivu (Articles)', label_ta: 'அனுபவ அறிவு (கட்டுரைகள்)', icon: '📰' },
    ],
  },
  {
    id: 'astrology',
    icon: '⭐',
    title_en: 'Astrology — Jothidam',
    title_ta: 'ஜோதிடம்',
    desc_en: 'Vakya Casting, Matching, Dasa Bukthi, and daily compatibility',
    desc_ta: 'வாக்கிய ஜாதகம், பொருத்தம், தசா புக்தி, தின பொருத்தம்',
    link: '/services',
    viewAllLink: '/services',
    viewAllText_en: 'All Services',
    viewAllText_ta: 'அனைத்து ஸேவைகள்',
    images: [
      '/images/rituals/Raasi-Mandalam.jpg',
      '/images/porutha-chakram.jpg',
      '/images/family-tree.jpg',
    ],
    subLinks: [
      { slug: '/services', label_en: 'All Services', label_ta: 'ஸேவைகள்', icon: '🔮' },
      { slug: '/panchangam/casting', label_en: 'Vakya Casting', label_ta: 'வாக்கிய ஜாதகம்', icon: '⭐' },
      { slug: '/panchangam/matching', label_en: 'Vakya Matching', label_ta: 'வாக்கிய பொருத்தம்', icon: '💑' },
      { slug: '/panchangam/dasabukthi', label_en: 'Dasa Bukthi', label_ta: 'தசா புக்தி', icon: '📊' },
    ],
  },
  {
    id: 'panchangam',
    icon: '📅',
    title_en: 'Panchangam — Daily Calendar',
    title_ta: 'பஞ்சாங்கம் — நாள்காட்டி',
    desc_en: 'Live Panchangam, Muhurthams, Good Days, Horai, and Gowri Panchangam',
    desc_ta: 'இன்றைய பஞ்சாங்கம், முகூர்த்தங்கள், சுப நாட்கள், ஹோரை',
    link: '/panchangam/panchangam',
    viewAllLink: '/panchangam/calendar',
    viewAllText_en: 'Full Calendar',
    viewAllText_ta: 'முழு காலண்டர்',
    images: [
      '/images/aavani-25.jpg',
      '/images/aadi-25.jpg',
      '/images/punyakalam.jpg',
    ],
    subLinks: [
      { slug: '/Sri_Paranthaman_Panchangam_Interactive_Browser.html', label_en: 'Sri Paranthaman Panchangam (Browser)', label_ta: 'ஸ்ரீ பரந்தாமன் பஞ்சாங்கம்', icon: '🌟' },
      { slug: '/Events-And-Shastra-Rules/', label_en: 'Shastric Rules (6,500+)', label_ta: 'சாஸ்திர விதிகள்', icon: '📜' },
      { slug: '/panchangam/panchangam', label_en: 'Live Panchangam', label_ta: 'இன்றைய பஞ்சாங்கம்', icon: '📅' },
      { slug: '/panchangam/calendar', label_en: 'Calendar', label_ta: 'காலண்டர்', icon: '🗓️' },
      { slug: '/panchangam/good-days', label_en: 'Good Days', label_ta: 'சுப நாட்கள்', icon: '✅' },
      { slug: '/panchangam/horai', label_en: 'Horai', label_ta: 'ஹோரை', icon: '⏰' },
      { slug: '/panchangam/gowri-panchangam', label_en: 'Gowri Panchangam', label_ta: 'கௌரி பஞ்சாங்கம்', icon: '🌺' },
    ],
  },
  {
    id: 'sastram',
    icon: '📖',
    title_en: 'Sastram — Scriptures',
    title_ta: 'சாஸ்திரம்',
    desc_en: 'Dharma Sastram (701 pages) and Pracheena Q&A — ancient wisdom',
    desc_ta: 'தர்ம சாஸ்திரம் (701 பக்கங்கள்), ப்ராசீன ஆசாரம்',
    link: '/dharma-sastram',
    viewAllLink: '/dharma-sastram',
    viewAllText_en: 'Read Dharma Sastram',
    viewAllText_ta: 'தர்ம சாஸ்திரம் படிக்க',
    images: [
      '/images/rituals/Sankalpam.jpg',
      '/images/rituals/pavitram.jpg',
      '/images/rituals/sthandilam.jpg',
    ],
    subLinks: [
      { slug: '/Events-And-Shastra-Rules/', label_en: 'Shastra Decision Rules (6,500+)', label_ta: 'சாஸ்திர முடிவெடுக்கும் விதிகள்', icon: '📜' },
      { slug: '/Sri_Paranthaman_Panchangam_Interactive_Browser.html', label_en: 'Sri Paranthaman Panchangam', label_ta: 'ஸ்ரீ பரந்தாமன் பஞ்சாங்கம்', icon: '📅' },
      { slug: '/dharma-sastram', label_en: 'Dharma Sastram (701 Pages)', label_ta: 'தர்ம சாஸ்திரம் (701 பக்கங்கள்)', icon: '📖' },
      { slug: '/pracheena-qa', label_en: 'Pracheena QA', label_ta: 'ப்ராசீன ஆசாரம்', icon: '🆕' },
    ],
  },
  {
    id: 'vaideekam',
    icon: '📿',
    title_en: 'Vaideekam — Vedic Rituals',
    title_ta: 'வைதீகம் — வைதீக கர்மாக்கள்',
    desc_en: 'Marriage, Upanayanam, Homam, Shradham, Tarpanam, Theetu, and more',
    desc_ta: 'திருமணம், உபநயனம், ஹோமம், ஸ்ராத்தம், தர்ப்பணம், தீட்டு',
    link: '/vaideekam',
    viewAllLink: '/vaideekam',
    viewAllText_en: 'Explore All Rituals',
    viewAllText_ta: 'அனைத்து கர்மாக்கள்',
    images: [
      '/images/rituals/aksharabhyasam.jpg',
      '/images/rituals/agni-kundam.jpg',
      '/images/rituals/namakaranam.jpg',
    ],
    subLinks: [
      { slug: '/vaideekam', label_en: 'Vedic Rituals', label_ta: 'வைதீக கர்மாக்கள்', icon: '📿' },
      { slug: '/panchangam/gen-sankalpam', label_en: 'General Sankalpam', label_ta: 'பொது சங்கல்பம்', icon: '🙏' },
      { slug: '/religious-services/shradhdha-service', label_en: 'Shradhdha Service', label_ta: 'ஸ்ராத்த ஸேவை', icon: '🪔' },
      { slug: '/panchangam/shradhdha-thithi', label_en: 'Shradhdha Thithi', label_ta: 'ஸ்ராத்த திதி', icon: '📋' },
      { slug: '/panchangam/sankalpam', label_en: 'Tarpanam Sankalpam', label_ta: 'தர்ப்பண சங்கல்பம்', icon: '💧' },
    ],
  },
  {
    id: 'upakarma',
    icon: '🔱',
    title_en: 'Upakarma & Special Rituals',
    title_ta: 'உபாகர்மா & சிறப்பு கர்மாக்கள்',
    desc_en: 'Rig, Yajur, Sama, Bodhayana Upakarma & Gayathri Japam guides',
    desc_ta: 'ரிக், யஜுர், சாம, போதாயன உபாகர்மா & காயத்ரி ஜபம்',
    link: '/yajur-upakarma',
    viewAllLink: '/vaideekam',
    viewAllText_en: 'All Upakarma',
    viewAllText_ta: 'அனைத்து உபாகர்மா',
    images: [
      '/images/rituals/surya-namaskaram-s.jpg',
      '/images/rituals/thiruman-kaappu.jpg',
      '/images/rituals/srichurnam-final.jpg',
    ],
    subLinks: [
      { slug: '/rig-upakarma', label_en: 'Rig Upakarma', label_ta: 'ரிக் உபாகர்மா', icon: '📜' },
      { slug: '/yajur-upakarma', label_en: 'Yajur Upakarma', label_ta: 'யஜுர் உபாகர்மா', icon: '📜' },
      { slug: '/sama-upakarma', label_en: 'Sama Upakarma', label_ta: 'சாம உபாகர்மா', icon: '📜' },
      { slug: '/bodhayana-upakarma', label_en: 'Bodhayana Upakarma', label_ta: 'போதாயன உபாகர்மா', icon: '📜' },
      { slug: '/gayathri-japam', label_en: 'Gayathri Japam', label_ta: 'காயத்ரி ஜபம்', icon: '🙏' },
    ],
  },
];

// ============================================================================
// SCROLL REVEAL HOOK
// ============================================================================
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);
  return ref;
};

// ============================================================================
// CONTENT CARD COMPONENT
// ============================================================================
const ContentCard = ({ image, title, excerpt, date, link, index }) => {
  return (
    <Link
      to={link}
      className={`content-card block animate-fade-in-up stagger-${(index % 4) + 1}`}
      style={{ opacity: 0, textDecoration: 'none' }}
    >
      <div className="overflow-hidden" style={{ height: '180px' }}>
        <img
          src={image}
          alt={title}
          className="card-image"
          onError={(e) => {
            e.target.src = '/images/home-page.jpg';
          }}
          loading="lazy"
        />
      </div>
      <div style={{ padding: '1rem' }}>
        <h3 style={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#1f2937',
          marginBottom: '0.5rem',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: 0,
        }}>
          {title}
        </h3>
        {excerpt && (
          <p style={{
            fontSize: '0.85rem',
            color: '#6b7280',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            margin: 0,
          }}>
            {excerpt}
          </p>
        )}
        {date && (
          <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
            {new Date(date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
          </p>
        )}
      </div>
    </Link>
  );
};

// ============================================================================
// CATEGORY SECTION COMPONENT
// ============================================================================
const CategorySection = ({ section, language, posts, isAlternate }) => {
  const ref = useScrollReveal();
  const sectionPosts = posts || [];

  // Merge real blog posts with static image cards if no posts
  const displayItems = sectionPosts.length > 0
    ? sectionPosts.slice(0, 3).map((post, i) => ({
        image: post.featured_image || post.image || section.images[i % section.images.length],
        title: language === 'en' ? (post.title_en || post.title) : (post.title_ta || post.title),
        excerpt: language === 'en' ? (post.excerpt_en || post.excerpt || '') : (post.excerpt_ta || post.excerpt || ''),
        date: post.published_at || post.created_at,
        link: post.slug ? `/blog/${post.slug}` : section.link,
      }))
    : section.images.map((img, i) => ({
        image: img,
        title: language === 'en' ? section.subLinks[i]?.label_en || section.title_en : section.subLinks[i]?.label_ta || section.title_ta,
        excerpt: '',
        date: null,
        link: section.subLinks[i]?.slug || section.link,
      }));

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: `2px solid var(--day-card-border, #e5e7eb)`,
      }}>
        <div>
          <h2 className="section-heading">
            <span style={{ fontSize: '1.8rem' }}>{section.icon}</span>
            {language === 'en' ? section.title_en : section.title_ta}
          </h2>
          <p style={{
            fontSize: '0.9rem',
            color: '#6b7280',
            marginTop: '0.75rem',
            marginBottom: 0,
          }}>
            {language === 'en' ? section.desc_en : section.desc_ta}
          </p>
        </div>
        <Link to={section.viewAllLink} className="view-all-link" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {language === 'en' ? section.viewAllText_en : section.viewAllText_ta} →
        </Link>
      </div>

      {/* Content Cards */}
      <div className="home-section-grid">
        {displayItems.map((item, idx) => (
          <ContentCard
            key={idx}
            image={item.image}
            title={item.title}
            excerpt={item.excerpt}
            date={item.date}
            link={item.link}
            index={idx}
          />
        ))}
      </div>

      {/* Quick Sub-links */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginTop: '1.25rem',
      }}>
        {section.subLinks.map((sub, i) => (
          <Link
            key={i}
            to={sub.slug}
            className="glass-card"
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: 'var(--day-text-highlight, #1f2937)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}
          >
            <span>{sub.icon}</span>
            {language === 'en' ? sub.label_en : sub.label_ta}
          </Link>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection = ({ theme, language }) => {
  return (
    <div
      className="hero-parallax"
      style={{
        background: 'linear-gradient(135deg, #0F5E56 0%, #0D524B 50%, #06403A 100%)',
        padding: '1.5rem 1rem 1.2rem 1rem',
        textAlign: 'center',
        position: 'relative',
        boxShadow: 'inner 0 0 50px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>

        {/* Subtitle */}
        <p
          className="animate-fade-in-up stagger-2"
          style={{
            fontSize: 'clamp(0.78rem, 1.75vw, 1.22rem)',
            color: '#FEF3C7',
            fontWeight: 800,
            marginBottom: '0.2rem',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            opacity: 0,
            textShadow: '0 2px 10px rgba(0,0,0,0.7)',
            letterSpacing: '0.01em',
          }}
        >
          {language === 'en'
            ? 'Vedic, Shastra, Sampradaya, Panchangam Assistance'
            : 'வைதீக, சாஸ்த்ர, ஸம்ப்ரதாய, பஞ்சாங்க, உதவி'}
        </p>
        <p
          className="animate-fade-in-up stagger-2"
          style={{
            fontSize: 'clamp(0.6rem, 1.2vw, 0.9rem)',
            color: '#D4AF37',
            fontWeight: 400,
            marginBottom: '0.8rem',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            opacity: 0,
            textShadow: '0 1px 6px rgba(0,0,0,0.5)',
            letterSpacing: '0.03em',
            fontStyle: 'italic',
          }}
        >
          {language === 'en'
            ? 'Authentic Information — 40 Years of Experience'
            : 'ஆதார பூர்வமான தகவல்கள் — 40 வருட அனுபவம்'}
        </p>

        {/* Quick action buttons & Inline Search - Single Line No Wrap */}
        <div
          className="animate-fade-in-up stagger-3"
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'clamp(0.4rem, 1vw, 0.75rem)',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            paddingBottom: '4px',
            opacity: 0,
            position: 'relative',
            zIndex: 50,
          }}
        >
          <Link
            to="/vaideekam"
            style={{
              padding: '0.5rem clamp(0.6rem, 1.2vw, 1.2rem)',
              background: '#8B4513',
              color: 'white',
              fontWeight: 700,
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontSize: 'clamp(0.78rem, 1.1vw, 0.9rem)',
              whiteSpace: 'nowrap',
              height: '42px',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(139,69,19,0.25)',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          >
            {language === 'en' ? '📿 Explore Rituals' : '📿 கர்மாக்கள்'}
          </Link>
          <Link
            to="/panchangam/panchangam"
            style={{
              padding: '0.5rem clamp(0.6rem, 1.2vw, 1.2rem)',
              background: '#2D5A1E',
              color: 'white',
              fontWeight: 700,
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontSize: 'clamp(0.78rem, 1.1vw, 0.9rem)',
              whiteSpace: 'nowrap',
              height: '42px',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(45,90,30,0.25)',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          >
            {language === 'en' ? "📅 Today's Panchangam" : '📅 பஞ்சாங்கம்'}
          </Link>
          <Link
            to="/qa"
            style={{
              padding: '0.5rem clamp(0.6rem, 1.2vw, 1.2rem)',
              background: '#CC6600',
              color: 'white',
              fontWeight: 700,
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontSize: 'clamp(0.78rem, 1.1vw, 0.9rem)',
              whiteSpace: 'nowrap',
              height: '42px',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(204,102,0,0.25)',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          >
            {language === 'en' ? '❓ Ask a Question' : '❓ கேள்வி'}
          </Link>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// QUICK STATS BAR
// ============================================================================
const QuickStatsBar = ({ publicStats, qaStats, language }) => {
  const ref = useScrollReveal();
  if (!publicStats) return null;

  const stats = [
    {
      value: publicStats.total_qa?.toLocaleString() || qaStats.total,
      label_en: 'Q&A Answered',
      label_ta: 'கேள்வி-பதில்கள்',
      icon: '💬',
      link: '/qa',
    },
    {
      value: publicStats.total_posts?.toLocaleString() || '200+',
      label_en: 'Articles',
      label_ta: 'கட்டுரைகள்',
      icon: '📰',
      link: '/blog',
    },
    {
      value: publicStats.total_comments?.toLocaleString() || '0',
      label_en: 'Comments',
      label_ta: 'உரையாடல்கள்',
      icon: '🗨️',
      link: null,
    },
    {
      value: publicStats.active_users || '0',
      label_en: 'Online Now',
      label_ta: 'தற்போது தளத்தில்',
      icon: '🟢',
      link: null,
      pulse: true,
    },
  ];

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '0.5rem 0' }}>
      <div className="glass-card" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        maxWidth: '900px',
        margin: '0 auto',
      }}>
        {stats.map((stat, i) => {
          const inner = (
            <div
              key={i}
              style={{
                textAlign: 'center',
                padding: '0.5rem',
                borderRight: i < 3 ? '1px solid var(--day-card-border, #e5e7eb)' : 'none',
              }}
            >
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{stat.icon}</div>
              <div style={{
                fontSize: 'clamp(1.3rem, 3vw, 2rem)',
                fontWeight: 300,
                color: 'var(--day-text-highlight, #1f2937)',
                letterSpacing: '-0.02em',
              }}>
                {stat.value}
              </div>
              <div style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}>
                {language === 'en' ? stat.label_en : stat.label_ta}
              </div>
            </div>
          );
          return stat.link
            ? <Link key={i} to={stat.link} style={{ textDecoration: 'none' }}>{inner}</Link>
            : <div key={i}>{inner}</div>;
        })}
      </div>
    </div>
  );
};

// ============================================================================
// QA HIGHLIGHT SECTION
// ============================================================================
const QAHighlightSection = ({ recentQa, qaStats, language, navigate }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid var(--day-card-border, #e5e7eb)',
      }}>
        <h2 className="section-heading">
          <span style={{ fontSize: '1.8rem' }}>💬</span>
          {language === 'en' ? 'Live Q&A Board' : 'கேள்வி-பதில் பகுதி'}
        </h2>
        <Link to="/qa" className="view-all-link">
          {language === 'en' ? 'Ask / View All' : 'கேள்வி கேட்க'} →
        </Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
        {/* Stats card */}
        <div className="glass-card" style={{
          padding: '2rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '3rem',
            fontWeight: 200,
            color: 'var(--day-primary, #E67E22)',
            lineHeight: 1,
          }}>
            {qaStats.total}
          </div>
          <div style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginTop: '0.5rem',
          }}>
            {language === 'en' ? 'Questions Answered' : 'பதில்கள் அளிக்கப்பட்டன'}
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1rem',
            padding: '0.35rem 1rem',
            background: '#ecfdf5',
            borderRadius: '2rem',
            fontSize: '0.8rem',
            fontWeight: 700,
            color: '#059669',
          }}>
            <span style={{ animation: 'pulseGlow 2s infinite' }}>●</span>
            {language === 'en' ? 'Replies within 2-24 hours' : '2-24 மணி நேரத்தில் பதில்'}
          </div>
          <Link
            to="/qa"
            style={{
              marginTop: '1.5rem',
              padding: '0.7rem 2rem',
              background: 'var(--day-primary, #E67E22)',
              color: 'white',
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              display: 'inline-block',
            }}
          >
            {language === 'en' ? 'Ask Now' : 'கேள்வி கேளுங்கள்'}
          </Link>
        </div>

        {/* Recent questions */}
        {recentQa.slice(0, 4).map((q, idx) => (
          <div
            key={idx}
            className="content-card"
            style={{ cursor: 'pointer', textDecoration: 'none' }}
            onClick={() => navigate('/qa')}
          >
            <div style={{ padding: '1.25rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
              }}>
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  padding: '0.2rem 0.6rem',
                  borderRadius: '1rem',
                  background: q.status === 'answered' ? '#dcfce7' : '#fef9c3',
                  color: q.status === 'answered' ? '#166534' : '#854d0e',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {q.status === 'answered' ? '✓ Answered' : '⏳ Pending'}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                  {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}
                </span>
              </div>
              <h3 style={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: '0.4rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                padding: 0,
              }}>
                {q.subject || q.title || 'Question'}
              </h3>
              <p style={{
                fontSize: '0.82rem',
                color: '#6b7280',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                margin: 0,
              }}>
                {sanitizePII(q.question) || ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// YOUTUBE SECTION
// ============================================================================
const YouTubeSection = ({ language }) => {
  const ref = useScrollReveal();
  const videoIds = ['GqpihbLnuD0', '6jIdlayX2Sc', 'F-5FX7kev_I', 'zkFGrk-K4iw', 'RZnodcZD8hI', 'XgSmzbAGqrk', '6p1KyvRzQ34'];

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid var(--day-card-border, #e5e7eb)',
      }}>
        <h2 className="section-heading">
          <span style={{ fontSize: '1.8rem' }}>🎬</span>
          {language === 'en' ? 'Ahobilam Vaideekam — YouTube' : 'அஹோபிலம் வைதீகம் — யூடியூப்'}
        </h2>
        <a
          href="https://www.youtube.com/vaideekam"
          target="_blank"
          rel="noopener noreferrer"
          className="view-all-link"
        >
          Subscribe →
        </a>
      </div>

      <div className="horizontal-scroll">
        {videoIds.map((videoId, index) => (
          <div
            key={index}
            style={{
              width: '320px',
              borderRadius: '1rem',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              background: '#000',
              flexShrink: 0,
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?modestbranding=1&rel=0`}
              title={`Ahobilam Vaideekam Video ${index + 1}`}
              style={{ width: '320px', height: '180px', border: 'none', display: 'block' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PHOTO GALLERY (enhanced)
// ============================================================================
const PhotoGallery = ({ language }) => {
  const ref = useScrollReveal();

  const galleryImages = [
    { url: '/images/rituals/kalyana-uthsavam.jpg', title_en: 'Traditional Wedding Ceremony', title_ta: 'பாரம்பரிய திருமண விழா' },
    { url: '/images/rituals/aksharabhyasam.jpg', title_en: 'Aksharabhyasam', title_ta: 'அக்ஷராப்யாசம்' },
    { url: '/images/rituals/agni-kundam.jpg', title_en: 'Sacred Homam', title_ta: 'புனித ஹோமம்' },
    { url: '/images/rituals/namakaranam.jpg', title_en: 'Namakaranam', title_ta: 'நாமகரணம்' },
    { url: '/images/rituals/naandhi.jpg', title_en: 'Naandhi Ritual', title_ta: 'நாந்தி சடங்கு' },
    { url: '/images/rituals/pavitram.jpg', title_en: 'Pavitram', title_ta: 'பவித்ரம்' },
    { url: '/images/sathya-narayana.jpg', title_en: 'Sathya Narayana Pooja', title_ta: 'சத்ய நாராயண பூஜை' },
    { url: '/images/rituals/vivaha-list.jpg', title_en: 'Vivaha Preparations', title_ta: 'விவாஹ ஏற்பாடுகள்' },
  ];

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid var(--day-card-border, #e5e7eb)',
      }}>
        <h2 className="section-heading">
          <span style={{ fontSize: '1.8rem' }}>📸</span>
          {language === 'en' ? 'Photo Gallery' : 'புகைப்பட தொகுப்பு'}
        </h2>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '0.75rem',
      }}>
        {galleryImages.map((image, index) => (
          <div
            key={index}
            className={`animate-scale-in stagger-${(index % 4) + 1}`}
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '0.75rem',
              aspectRatio: '1/1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
              cursor: 'pointer',
              opacity: 0,
            }}
          >
            <img
              src={image.url}
              alt={language === 'en' ? image.title_en : image.title_ta}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.5s ease',
              }}
              loading="lazy"
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            />
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(40,15,5,0.75) 0%, transparent 60%)',
              opacity: 0,
              transition: 'opacity 0.3s ease',
              display: 'flex',
              alignItems: 'flex-end',
              padding: '0.75rem',
            }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
              onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
            >
              <p style={{
                color: 'white',
                fontWeight: 600,
                fontSize: '0.85rem',
                margin: 0,
              }}>
                {language === 'en' ? image.title_en : image.title_ta}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// ABOUT SECTION
// ============================================================================
const AboutSection = ({ language }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '3rem 0' }}>
      <div className="glass-card" style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '3rem 2rem',
        textAlign: 'center',
      }}>
        <h2 className="section-heading" style={{
          justifyContent: 'center',
          fontSize: '1.8rem',
          marginBottom: '1rem',
        }}>
          {language === 'en' ? 'About Ahobilam.com' : 'அஹோபிலம்.காம் பற்றி'}
        </h2>
        <p style={{
          fontSize: '1.05rem',
          color: '#4b5563',
          lineHeight: 1.8,
          maxWidth: '600px',
          margin: '1rem auto 0',
        }}>
          {language === 'en'
            ? 'Ahobilam.com has been serving the Sri Vaishnava community since August 19, 1989, providing authentic information about Vedic rituals, mantras, and procedures in Sanskrit, Tamil, and English. Our mission is to preserve and share traditional knowledge with everyone, completely free of cost.'
            : 'அஹோபிலம்.காம் 1989 ஆகஸ்ட் 19 ஆம் தேதி முதல் ஸ்ரீ வைஷ்ணவ சமூகத்திற்கு சேவை செய்து வருகிறது, வைதீக கர்மாக்கள், மந்திரங்கள் மற்றும் நடைமுறைகள் பற்றிய உண்மையான தகவல்களை சமஸ்கிருதம், தமிழ் மற்றும் ஆங்கிலத்தில் வழங்குகிறது.'}
        </p>
      </div>
    </div>
  );
};

// ============================================================================

// ============================================================================
// PREMIER FEATURED SHOWCASE COMPONENT (Panchangam & Shastra Rules)
// ============================================================================
const PremierFeaturedShowcase = ({ language }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2rem 0 1rem 0' }}>
      {/* Section Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: '#e8f5e9',
          color: '#1b5e20',
          padding: '4px 14px',
          borderRadius: '20px',
          fontSize: '12.5px',
          fontWeight: '800',
          border: '1px solid #c8e6c9',
          marginBottom: '8px'
        }}>
          <span>✨</span>
          <span>{language === 'en' ? 'Ahobilam Special Features' : 'அஹோபிலம் சிறப்பு வெளியீடுகள்'}</span>
        </div>
        <h2 style={{
          fontSize: 'clamp(1.3rem, 2.5vw, 1.85rem)',
          fontWeight: 800,
          color: '#123d16',
          letterSpacing: '-0.01em',
          margin: 0,
        }}>
          {language === 'en'
            ? '🌟 Sri Paranthaman Panchangam & Shastra Decision Rules'
            : '🌟 ஸ்ரீ பரந்தாமன் பஞ்சாங்கம் & சாஸ்திர விசேஷ முடிவெடுக்கும் விதிகள்'}
        </h2>
        <p style={{
          fontSize: '0.9rem',
          color: '#4e7352',
          marginTop: '0.4rem',
          marginBottom: 0,
        }}>
          {language === 'en'
            ? 'Authentic 2-Year Interactive Landscape Panchangam & 6,500+ Shastric Decision Rules Catalog'
            : '2026–2028 நேரடி வாக்கிய பஞ்சாங்கம் மற்றும் 6,500+ விசேஷங்களுக்கான சாஸ்திர ஆதார விதிகள்'}
        </p>
      </div>

      {/* Two Premium Preview Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1.5rem',
      }}>

        {/* CARD 1: SRI PARANTHAMAN PANCHANGAM BROWSER */}
        <div className="glass-card" style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #f4faf4 100%)',
          border: '2px solid #2e7d32',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 8px 24px rgba(27, 94, 32, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(27, 94, 32, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(27, 94, 32, 0.12)';
        }}
        >
          {/* Top Banner Stripe */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #1b5e20, #d4af37, #1b5e20)'
          }}></div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <span style={{
                background: '#fff8e1',
                color: '#8d6e00',
                border: '1.2px solid #ffe082',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '11.5px',
                fontWeight: '800'
              }}>
                📅 2026–2028 (பராபவ • பிலவங்க)
              </span>
              <span style={{ fontSize: '26px' }}>🕉️</span>
            </div>

            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 800,
              color: '#123d16',
              marginBottom: '8px',
              lineHeight: 1.3
            }}>
              {language === 'en'
                ? 'Sri Paranthaman Panchangam (Interactive Browser)'
                : 'ஸ்ரீ பரந்தாமன் பஞ்சாங்கம் (நேரடி வாக்கிய பஞ்சாங்கம்)'}
            </h3>

            <p style={{
              fontSize: '0.86rem',
              color: '#2e5b33',
              lineHeight: 1.5,
              marginBottom: '14px'
            }}>
              {language === 'en'
                ? 'Full 12-month landscape panchangam view. Hover over any event to see exact Shastric decision rules and time windows. Print monthly pages in high-resolution A4 landscape.'
                : 'மாதவாரியாக 12 மாத முழு லேண்ட்ஸ்கேப் பஞ்சாங்க அட்டவணை. விசேஷங்களின் மீது மவுஸ் வைத்தால் முழுமையான சாஸ்திர முடிவெடுக்கும் விதி பாப்அப். A4 அச்சு வசதி.'}
            </p>

            <div style={{
              background: '#edf7ee',
              border: '1px dashed #a5d6a7',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '16px',
              fontSize: '12px',
              color: '#1b5e20',
              lineHeight: 1.6
            }}>
              <div>✓ <b>தினசரி பஞ்சாங்கம்:</b> திதி, நக்ஷத்திரம், யோகம், கரணம், ச்ராத்த திதி</div>
              <div>✓ <b>சாஸ்திர விதி பாப்அப்:</b> உதய / பிரதோஷ / நிசீத வியாப்தி முடிவுகள்</div>
              <div>✓ <b>A4 அச்சு:</b> ஒற்றை மாதம் அல்லது 12 மாத புத்தகமாக அச்சிடும் வசதி</div>
            </div>
          </div>

          <a
            href="/Sri_Paranthaman_Panchangam_Interactive_Browser.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'linear-gradient(135deg, #1b5e20 0%, #0d3810 100%)',
              color: '#ffffff',
              border: '1.5px solid #d4af37',
              padding: '10px 16px',
              borderRadius: '10px',
              fontWeight: 800,
              fontSize: '13.5px',
              textDecoration: 'none',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(27, 94, 32, 0.25)',
              transition: 'all 0.2s ease',
            }}
          >
            <span>{language === 'en' ? 'Open Panchangam Browser' : 'பஞ்சாங்கம் நேரடி பார்வை திறக்க'}</span>
            <span>↗</span>
          </a>
        </div>

        {/* CARD 2: EVENTS & SHASTRA RULES ENGINE */}
        <div className="glass-card" style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #fffdf4 100%)',
          border: '2px solid #d4af37',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 8px 24px rgba(184, 134, 11, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(184, 134, 11, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(184, 134, 11, 0.12)';
        }}
        >
          {/* Top Banner Stripe */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #d4af37, #1b5e20, #d4af37)'
          }}></div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <span style={{
                background: '#e8f5e9',
                color: '#1b5e20',
                border: '1.2px solid #a5d6a7',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '11.5px',
                fontWeight: '800'
              }}>
                📜 6,500+ சாஸ்திர முடிவெடுக்கும் விதிகள்
              </span>
              <span style={{ fontSize: '26px' }}>⚖️</span>
            </div>

            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 800,
              color: '#7a5a00',
              marginBottom: '8px',
              lineHeight: 1.3
            }}>
              {language === 'en'
                ? 'Events & Shastra Decision Rules (6,500+ Rules Engine)'
                : 'பஞ்சாங்க சாஸ்திர விசேஷங்கள் & முடிவெடுக்கும் விதிகள்'}
            </h3>

            <p style={{
              fontSize: '0.86rem',
              color: '#54410a',
              lineHeight: 1.5,
              marginBottom: '14px'
            }}>
              {language === 'en'
                ? 'Exhaustive searchable catalog of 6,500+ events across all 12 months with Nirnaya Sindhu, Dharma Shastram, and Vaidyanatha Dikshitiyam rules.'
                : 'நிர்ணய ஸிந்து, தர்ம சாஸ்திரம் மற்றும் வைத்யநாத தீக்ஷிதீயம் ஆதாரங்களுடன் 12 மாதங்களின் அனைத்து விசேஷங்கள் மற்றும் விரதங்களுக்கான சாஸ்திர தீர்ப்புக் களஞ்சியம்.'}
            </p>

            <div style={{
              background: '#fff9e6',
              border: '1px dashed #ffe082',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '16px',
              fontSize: '12px',
              color: '#8c5b00',
              lineHeight: 1.6
            }}>
              <div>✓ <b>உடனடி தேடல்:</b> 12 மாதங்களின் விசேஷங்கள், ஆழ்வார் ஆசார்ய திருநக்ஷத்திரங்கள்</div>
              <div>✓ <b>சாஸ்திர வியாப்தி:</b> உத்தம கால அளவு, இரு தின வியாப்தி/அவியாப்தி விதிகள்</div>
              <div>✓ <b>பிரமாண சுலோகங்கள்:</b> மூல சமஸ்கிருத பிரமாண சுலோகம் மற்றும் தமிழ் விளக்கம்</div>
            </div>
          </div>

          <a
            href="/Events-And-Shastra-Rules/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #a37c00 100%)',
              color: '#123d16',
              border: '1.5px solid #ffffff',
              padding: '10px 16px',
              borderRadius: '10px',
              fontWeight: 800,
              fontSize: '13.5px',
              textDecoration: 'none',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(184, 134, 11, 0.25)',
              transition: 'all 0.2s ease',
            }}
          >
            <span>{language === 'en' ? 'Explore Shastra Rules Engine' : 'சாஸ்திர விதிகள் களஞ்சியத்தைத் திறக்க'}</span>
            <span>↗</span>
          </a>
        </div>

      </div>
    </div>
  );
};

// MAIN DYNAMIC HOME PAGE COMPONENT
// ============================================================================

// ============================================================================
// UPAKARMA & GAYATHRI JAPAM FESTIVAL SPECIAL SHOWCASE
// ============================================================================
const UpakarmaFestivalBanner = ({ language }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '1.2rem 0 0.5rem 0' }}>
      <div style={{
        background: 'linear-gradient(135deg, #4a154b 0%, #6b1d2f 50%, #8b263e 100%)',
        borderRadius: '20px',
        padding: 'clamp(1.2rem, 2.5vw, 2rem)',
        color: '#ffffff',
        boxShadow: '0 12px 35px rgba(107, 29, 47, 0.25)',
        border: '2px solid #d4af37',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Gold Corner Accents */}
        <div style={{
          position: 'absolute', top: '-15px', right: '-15px', width: '90px', height: '90px',
          background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none'
        }} />

        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#fef3c7',
            padding: '5px 16px',
            borderRadius: '25px',
            fontSize: '12.5px',
            fontWeight: 800,
            border: '1px solid rgba(212, 175, 55, 0.5)',
            marginBottom: '10px'
          }}>
            <span>✨</span>
            <span>{language === 'en' ? 'Aavani Avittam Special • Upakarma 2026' : 'ஆவணி அவிட்டம் சிறப்பு • உபாகர்மா & காயத்ரி ஜபம்'}</span>
          </div>

          <h2 style={{
            fontSize: 'clamp(1.3rem, 2.5vw, 2rem)',
            fontWeight: 900,
            color: '#ffffff',
            margin: '0 0 8px 0',
            textShadow: '0 2px 8px rgba(0,0,0,0.4)'
          }}>
            {language === 'en'
              ? '🕉️ Yajur, Bodhayana Upakarma & Gayathri Maha Japam'
              : '🕉️ யஜுர், போதாயன உபாகர்மா & காயத்ரி மஹா ஜப பிரயோகம்'}
          </h2>

          <p style={{
            fontSize: 'clamp(0.85rem, 1.2vw, 0.98rem)',
            color: '#fed7aa',
            maxWidth: '750px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            {language === 'en'
              ? 'Complete authentic procedures, Maha Sankalpam, Kandarishi Tarpanam, Yajnopaveetha Dharana Mantras, and Gayathri 1008/108 Japam.'
              : 'பூணூல் மாற்றும் மந்திரங்கள், மஹா சங்கல்பம், காண்டரிஷி தர்ப்பணம், ப்ரஹ்மயஜ்ஞம் மற்றும் 1008/108 காயத்ரி ஜப முழு பிரயோக கையேடு.'}
          </p>
        </div>

        {/* 3 Prominent Featured Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.2rem',
        }}>

          {/* CARD 1: YAJUR UPAKARMA */}
          <div style={{
            background: '#ffffff',
            color: '#1f2937',
            borderRadius: '16px',
            padding: '1.4rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            borderTop: '5px solid #d97706',
            transition: 'transform 0.2s ease',
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{
                  background: '#fef3c7', color: '#92400e', padding: '3px 10px',
                  borderRadius: '10px', fontSize: '11.5px', fontWeight: 800
                }}>
                  {language === 'en' ? 'Yajur Veda' : 'யஜுர் வேதம்'}
                </span>
                <span style={{ fontSize: '24px' }}>🕉️</span>
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#92400e', marginBottom: '8px' }}>
                {language === 'en' ? 'Yajur Upakarma' : 'யஜுர் உபாகர்மா'}
              </h3>
              <p style={{ fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.5, marginBottom: '12px' }}>
                {language === 'en'
                  ? 'Yajnopaveetha Dharanam, Kandarishi Tarpanam, Brahmayajnam, and Samidadhanam mantras.'
                  : 'பூணூல் மாற்றுதல், காண்டரிஷி தர்ப்பணம், மஹா சங்கல்பம், ப்ரஹ்மயஜ்ஞம் & சமிதாதானம்.'}
              </p>
            </div>
            <Link
              to="/vaideekam/yajur-upakarma"
              style={{
                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                color: '#ffffff',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 3px 8px rgba(217, 119, 6, 0.3)'
              }}
            >
              <span>{language === 'en' ? 'Open Yajur Prayogam' : 'யஜுர் பிரயோகம் படிக்க'}</span>
              <span>➔</span>
            </Link>
          </div>

          {/* CARD 2: BODHAYANA UPAKARMA */}
          <div style={{
            background: '#ffffff',
            color: '#1f2937',
            borderRadius: '16px',
            padding: '1.4rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            borderTop: '5px solid #16a34a',
            transition: 'transform 0.2s ease',
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{
                  background: '#dcfce7', color: '#166534', padding: '3px 10px',
                  borderRadius: '10px', fontSize: '11.5px', fontWeight: 800
                }}>
                  {language === 'en' ? 'Bodhayana Sutram' : 'போதாயன சூத்ரம்'}
                </span>
                <span style={{ fontSize: '24px' }}>📜</span>
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#166534', marginBottom: '8px' }}>
                {language === 'en' ? 'Bodhayana Upakarma' : 'போதாயன உபாகர்மா'}
              </h3>
              <p style={{ fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.5, marginBottom: '12px' }}>
                {language === 'en'
                  ? 'Bodhayana Maha Sankalpam, Bodhayana Brahmayajnam, and specific Kandarishi Tarpanam.'
                  : 'போதாயன மஹா சங்கல்பம், போதாயன ப்ரஹ்மயஜ்ஞம் மற்றும் விசேஷ காண்டரிஷி தர்ப்பண முறைகள்.'}
              </p>
            </div>
            <Link
              to="/vaideekam/bodhayana-upakarma"
              style={{
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                color: '#ffffff',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 3px 8px rgba(22, 163, 74, 0.3)'
              }}
            >
              <span>{language === 'en' ? 'Open Bodhayana Prayogam' : 'போதாயன பிரயோகம் படிக்க'}</span>
              <span>➔</span>
            </Link>
          </div>

          {/* CARD 3: GAYATHRI JAPAM */}
          <div style={{
            background: '#ffffff',
            color: '#1f2937',
            borderRadius: '16px',
            padding: '1.4rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            borderTop: '5px solid #7c3aed',
            transition: 'transform 0.2s ease',
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{
                  background: '#f3e8ff', color: '#6b21a8', padding: '3px 10px',
                  borderRadius: '10px', fontSize: '11.5px', fontWeight: 800
                }}>
                  {language === 'en' ? 'Gayathri Japam' : 'காயத்ரி மஹா ஜபம்'}
                </span>
                <span style={{ fontSize: '24px' }}>📿</span>
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#6b21a8', marginBottom: '8px' }}>
                {language === 'en' ? 'Gayathri Maha Japam' : 'காயத்ரி மஹா ஜபம்'}
              </h3>
              <p style={{ fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.5, marginBottom: '12px' }}>
                {language === 'en'
                  ? 'Gayathri Avahanam, Nyasam, 1008/108 Japam Sankalpam, Upasthanam, and Dikhvandanam.'
                  : 'காயத்ரி ஆவாஹனம், ந்யாஸம், 1008/108 ஜப சங்கல்பம், உபஸ்தானம் மற்றும் திக்வந்தனம்.'}
              </p>
            </div>
            <Link
              to="/vaideekam/gayathrijapam"
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: '#ffffff',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 3px 8px rgba(124, 58, 237, 0.3)'
              }}
            >
              <span>{language === 'en' ? 'Open Gayathri Japam' : 'காயத்ரி ஜபம் படிக்க'}</span>
              <span>➔</span>
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};

const DynamicHomePage = ({ language, user, navigate, CommentSection, ForgotPasswordModal }) => {
  const theme = getTodayTheme();
  const [qaStats, setQaStats] = useState({ total: '3400+' });
  const [recentQa, setRecentQa] = useState([]);
  const [publicStats, setPublicStats] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Fetch data
  useEffect(() => {
    // Q&A stats
    axios.get(`${API}/qa/list?limit=30`)
      .then(res => {
        if (res.data) {
          if (res.data.total) setQaStats({ total: res.data.total });
          if (res.data.items) setRecentQa(res.data.items);
          else if (res.data.questions) setRecentQa(res.data.questions);
        }
      })
      .catch(() => {});

    // Public stats
    axios.get(`${API}/analytics/public-stats`)
      .then(res => setPublicStats(res.data))
      .catch(() => {});

    // Blog posts (for content sections)
    axios.get(`${API}/blog/posts?limit=20`)
      .then(res => setBlogPosts(res.data || []))
      .catch(() => {});

    // Admin redirect
    const params = new URLSearchParams(window.location.search);
    if ((params.get('admin') === 'true' || localStorage.getItem('redirectToAdmin') === 'true')
        && user && (user.role === 'admin' || user.role === 'moderator')) {
      localStorage.removeItem('redirectToAdmin');
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  // Forgot password event
  useEffect(() => {
    const handleFP = () => setShowForgotPassword(true);
    window.addEventListener('openForgotPassword', handleFP);
    return () => window.removeEventListener('openForgotPassword', handleFP);
  }, []);

  return (
    <div className={`min-h-screen ${theme.className}`} style={{ background: 'var(--day-bg-gradient, #f9fafb)' }}>

      {/* Hero Section */}
      <HeroSection theme={theme} language={language} />

      {/* Upakarma & Gayathri Japam Festive Special Showcase */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <UpakarmaFestivalBanner language={language} />
      </div>

      {/* Premier Featured Showcase: Panchangam & 6,500+ Shastra Decision Rules */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <PremierFeaturedShowcase language={language} />
      </div>

      {/* Main Category Sections — Immediately Below Hero */}
      <div style={{ maxWidth: '1200px', margin: '1rem auto 0', padding: '0 1rem' }}>
        {CATEGORY_SECTIONS.map((section, index) => (
          <React.Fragment key={section.id}>
            <CategorySection
              section={section}
              language={language}
              posts={blogPosts.filter(p =>
                (p.categories || []).some(c =>
                  (typeof c === 'string' ? c : c.name || '').toLowerCase().includes(section.id.toLowerCase())
                )
              )}
              isAlternate={index % 2 === 1}
            />
            {index < CATEGORY_SECTIONS.length - 1 && (
              <div className="section-divider" style={{ margin: '0 auto' }}></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Section Divider */}
      <div className="section-divider" style={{ margin: '1rem auto' }}></div>

      {/* Q&A Highlight */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <QAHighlightSection recentQa={recentQa} qaStats={qaStats} language={language} navigate={navigate} />
      </div>

      {/* Section Divider */}
      <div className="section-divider" style={{ margin: '1rem auto' }}></div>

      {/* YouTube */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <YouTubeSection language={language} />
      </div>

      {/* Section Divider */}
      <div className="section-divider" style={{ margin: '1rem auto' }}></div>

      {/* Photo Gallery */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <PhotoGallery language={language} />
      </div>

      {/* About Section */}
      <div className="kolam-pattern" style={{ background: 'var(--day-bg-gradient, #f0fdf4)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', position: 'relative', zIndex: 1 }}>
          <AboutSection language={language} />
        </div>
      </div>

      {/* Comments */}
      {CommentSection && (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
          <CommentSection pageSlug="home" />
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && ForgotPasswordModal && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </div>
  );
};

export default DynamicHomePage;
export { getTodayTheme, DAY_THEMES };
